import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { siteImages, CONTACT_INFO } from '../data/mock';
import { Zap, Target, Heart, Award, Shield, ArrowRight, Check } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Keto Vybe | Our Mission';
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-72 lg:h-96 overflow-hidden">
          <img 
            src={siteImages.aboutBanner}
            alt="About"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Our Story</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                About Keto Vybe
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Premium supplements for your keto journey
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-4 block">Our Mission</span>
                <h2 className="font-display text-4xl text-white mb-6">
                  Fueling Your Potential
                </h2>
                <p className="text-slate-400 leading-relaxed mb-6">
                  At Keto Vybe, we're passionate about helping people achieve their health and wellness goals through the ketogenic lifestyle. We believe in the power of quality supplements to support your journey.
                </p>
                <p className="text-slate-400 leading-relaxed">
                  Our team is dedicated to sourcing the best ingredients, testing rigorously, and delivering products that truly make a difference. We're not just selling supplements – we're supporting a community of people committed to better health.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden">
                  <img 
                    src={siteImages.heroSecondary}
                    alt="Active lifestyle"
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-vybe-600 to-coral-500 rounded-2xl flex items-center justify-center">
                  <Zap className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-cyan-400 font-semibold text-sm tracking-wider uppercase mb-3 block">What We Stand For</span>
              <h2 className="font-display text-4xl text-white mb-4">Our Values</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Award, title: 'Quality First', desc: 'We source premium ingredients and test every batch for purity and potency.', color: 'vybe' },
                { icon: Heart, title: 'Customer Focus', desc: 'Your success is our success. We\'re here to support your keto journey.', color: 'coral' },
                { icon: Shield, title: 'Science-Backed', desc: 'Our formulas are based on research and developed by nutrition experts.', color: 'cyan' },
              ].map((value, i) => (
                <div key={i} className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/50 hover:border-vybe-500/30 transition-all">
                  <div className={`w-14 h-14 bg-${value.color}-600/20 rounded-xl flex items-center justify-center mb-5`}>
                    <value.icon className={`w-7 h-7 text-${value.color}-400`} />
                  </div>
                  <h3 className="font-bold text-white text-xl mb-3">{value.title}</h3>
                  <p className="text-slate-400">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-3xl overflow-hidden">
                  <img 
                    src={siteImages.lifestyleSection}
                    alt="Wellness"
                    className="w-full h-80 object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-4 block">The Vybe Difference</span>
                <h2 className="font-display text-4xl text-white mb-6">
                  Why Choose Keto Vybe
                </h2>
                <ul className="space-y-4 mb-8">
                  {[
                    'Formulas optimized for ketogenic diets',
                    'Third-party tested for quality assurance',
                    'Made in USA in GMP-certified facilities',
                    'Clean ingredients without unnecessary fillers',
                    'Fast, free shipping on orders $99+',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <div className="w-6 h-6 bg-vybe-600/20 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-vybe-400" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/shop"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full hover:shadow-glow-purple transition-all"
                >
                  Shop Products
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-slate-950 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-mesh opacity-20" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-vybe-600 to-coral-500 rounded-2xl mb-8">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-4xl text-white mb-4">
              Ready to Start?
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
              Browse our premium keto supplements and find what works for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/shop"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full"
              >
                Shop Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-full border border-white/20 hover:bg-white/20 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
